import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, OnInit, AfterViewInit, Inject, ElementRef, ViewChild
} from '@angular/core';
import { Observable, map } from 'rxjs';

import { TranslatePipe } from '@app/translate/TranslatePipe';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { ProvidersExcludedFromDuration } from '@shared/flight-segment-connection/entities/segment-types';
import { AirValidationSector, AirValidationFlightsSector } from '@app/entities/air-validation.response';
import { TripTypes } from '@app/entities/trip-types';
import { SectorTripNumber } from '@user-history/entities/user-history-item';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { luggageTooltipParentTypes } from '@ux/entities/tooltip-types';
import { FlightSectorAirValidationSector } from '@booking/entities/flight-sectors.models';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { Price } from '@app/entities/price';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { isNullOrUndefined } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { environment as ENV } from '@environment';
import { Countries } from '@ux/entities/country.enum';

@Component({
  selector: 'app-flight-sectors',
  templateUrl: './flight-sectors.component.html',
  styleUrls: ['./flight-sectors.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlightSectorsComponent implements OnInit, AfterViewInit {

  @ViewChild('bodycontainer') public bodyContainer: ElementRef;
  @ViewChild('container') public container: ElementRef;
  @Input() isMobile: boolean;
  @Input() airValidationSector: FlightSectorAirValidationSector[];
  @Input() flightClass: FlightClass;
  @Input() provider: string;
  @Input() travelType: TravelBookingType;
  @Input() sectorSelected: number;
  @Input() price: Price;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() isLoadingFlightCabin: boolean = false;
  @Input() conversionRate: number;
  @Input() isInternational: boolean;
  @Input() productReference: number;
  
  public isCollapsed: boolean = true;
  public flightInfoProvider: TravelDetailProvider = TravelDetailProvider.BOOKING_FLIGHT;
  public parentCoint: luggageTooltipParentTypes = luggageTooltipParentTypes.COINT;
  private departureSectorNumber: SectorTripNumber = SectorTripNumber.DEPARTURE;

  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly appFacade: AppFacade,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  ngOnInit(): void {
    this.flightInfoProvider =
      this.travelType === TravelBookingType.BUSES ? TravelDetailProvider.BOOKING_BUSES : TravelDetailProvider.BOOKING_FLIGHT;
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      const positionSectorSelected = this.airValidationSector.findIndex(route => route.SectorNumber === this.sectorSelected);
      if (positionSectorSelected > -1) {
        this.scrollToPosition(this.bodyContainer.nativeElement.children[positionSectorSelected].offsetTop);
      }
    }
  }

  get isRappiProBrandig$(): Observable<boolean> {
    return this.appFacade.isPrime$.pipe(map(isPrime => isPrime && ENV.countryInfo.iso_code === Countries.Colombia));
  }

  get isExcludedFromDuration(): boolean {
    return Object.values(
      ProvidersExcludedFromDuration).includes(
        this.provider as ProvidersExcludedFromDuration);
  }

  public hasScales(airValidationSector: AirValidationSector): boolean {
    return airValidationSector.Flights.length - 1 > 0;
  }

  public justOneScale(airValidationSector: AirValidationSector): boolean {
    return (airValidationSector.Flights.length - 1) === 1;
  }

  public scalesNumber(airValidationSector: AirValidationSector): number {
    return airValidationSector ? airValidationSector.Flights.length - 1 : 0;
  }

  public isScaleElement(airValidationSector: AirValidationSector, index: number): boolean {
    return index < airValidationSector.Flights.length - 1;
  }

  public showDivider(index: number): boolean {
    return (this.airValidationSector.length - 1) > index;
  }

  public flightType(airValidationSector: AirValidationSector): string {
    return this.translatePipe.transform(
      `FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.${
        (this.airValidationSector?.length > 1 ? airValidationSector.SectorNumber : 
          !isNullOrUndefined(this.sectorSelected) ? this.sectorSelected: this.departureSectorNumber) === TripTypes.DEPARTURE ?
          'DEPARTURE_TRIP' : 'RETURN_TRIP'}`
    );
  }

  public flightTrip(airValidationSector: AirValidationSector): string {
    return `${airValidationSector.Flights[0].DepartureAirport} -
    ${airValidationSector.Flights[airValidationSector.Flights.length - 1].ArrivalAirport}`;
  }

  public flightDepartureDate(airValidationSector: AirValidationSector): string {
    return airValidationSector.Flights[0].DepartureDate;
  }

  public flightBaggage(airValidationSector: AirValidationSector): string {
    return airValidationSector.Flights[0].BaggageRestrictions;
  }

  public flightItem(sector: AirValidationSector, item: number): AirValidationFlightsSector {
    return sector.Flights[item];
  }

  public nextFlightItem(sector: AirValidationSector, item: number): AirValidationFlightsSector {
    return sector.Flights[item + 1] || {} as AirValidationFlightsSector;
  }

  public toggleTravelInfo(sector: FlightSectorAirValidationSector): void {
    sector.isExpanded = !sector.isExpanded;
  }

  public scrollToPosition(top: number): void {
    this.bodyContainer.nativeElement.scrollTo({
      left: 0, top: top
    });
  }

}
